/**
 * useBookingDrag.ts
 * Grid-level, multi-mode drag engine for MachineBooking bars
 * (Machine Schedule UX overhaul — M2).
 *
 * Modes:
 *   - 'resize-start' → drag the left edge to change StartDate
 *   - 'resize-end'   → drag the right edge to change EndDate
 *   - 'move'         → drag the body to shift both dates (horizontal) and/or
 *                      move to another machine row (vertical hit-test)
 *
 * The hook lives at the grid level (not per-bar) because a move can cross rows:
 * the grid renders a single floating ghost in the *target* row from `activeDrag`.
 * A body press that never moves past CLICK_THRESHOLD px is treated as a click
 * (→ onClick), so single-click opens the detail drawer without a double-click.
 *
 * Cross-machine moves are constrained to capable machines: if the target row is
 * not capable of the booking's task type, `activeDrag.valid` is false and the
 * drop is rejected (onInvalidDrop) instead of committed.
 *
 * Uses Pointer Events + window listeners so mouse/touch/pen behave uniformly.
 * Commit + persistence + undo happen in the caller's onCommit (useScheduleMutations).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { MachineBookingCalendar } from '../types/schedule';
import type { BookingPlacementPatch } from './useScheduleData';

export type DragMode = 'resize-start' | 'resize-end' | 'move';

/** Below this pointer travel (px) a body press counts as a click, not a drag. */
const CLICK_THRESHOLD = 4;

export interface ActiveDrag {
  bookingKey: string;
  mode: DragMode;
  /** True once the pointer has travelled past CLICK_THRESHOLD. */
  moved: boolean;
  ghostStart: string;
  ghostEnd: string;
  ghostMachineKey: string;
  originalMachineKey: string;
  /** False when the hovered target machine can't run the booking's task type. */
  valid: boolean;
  pointerX: number;
  pointerY: number;
}

interface UseBookingDragArgs {
  dayWidthPx: number;
  /** capable machineKeys for a booking, or null when any machine is allowed. */
  getCapableMachineKeys: (booking: MachineBookingCalendar) => Set<string> | null;
  /** Resolve a machine's display code (for the commit label). */
  getMachineCode: (machineKey: string) => string;
  onCommit: (
    booking: MachineBookingCalendar,
    patch: BookingPlacementPatch,
    label: string,
  ) => void;
  onClick: (booking: MachineBookingCalendar) => void;
  onInvalidDrop: (message: string) => void;
}

interface UseBookingDragResult {
  activeDrag: ActiveDrag | null;
  beginDrag: (
    booking: MachineBookingCalendar,
    mode: DragMode,
    e: React.PointerEvent,
  ) => void;
}

/** Offset a 'YYYY-MM-DD' string by `deltaDays` days. */
function shiftDate(isoDate: string, deltaDays: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** Whole days between two ISO dates (endIso - startIso). */
function daysBetween(startIso: string, endIso: string): number {
  const a = new Date(`${startIso}T00:00:00`).getTime();
  const b = new Date(`${endIso}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

function bookingName(b: MachineBookingCalendar): string {
  return b.TaskName ?? b.EventType ?? 'booking';
}

export function useBookingDrag(args: UseBookingDragArgs): UseBookingDragResult {
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);

  // Keep args + per-drag origin in refs so global listeners avoid stale closures.
  const argsRef = useRef(args);
  useEffect(() => { argsRef.current = args; }, [args]);

  const bookingRef = useRef<MachineBookingCalendar | null>(null);
  const modeRef = useRef<DragMode>('move');
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const origStartRef = useRef('');
  const origEndRef = useRef('');
  const origMachineRef = useRef('');
  const durationRef = useRef(0);
  const capableRef = useRef<Set<string> | null>(null);
  const latestRef = useRef<ActiveDrag | null>(null);

  const setDrag = useCallback((next: ActiveDrag | null) => {
    latestRef.current = next;
    setActiveDrag(next);
  }, []);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const booking = bookingRef.current;
    const prev = latestRef.current;
    if (!booking || !prev) return;

    const dpx = argsRef.current.dayWidthPx;
    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;
    const moved = prev.moved || Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD;
    const deltaDays = dpx > 0 ? Math.round(dx / dpx) : 0;

    let ghostStart = origStartRef.current;
    let ghostEnd = origEndRef.current;
    let ghostMachineKey = prev.ghostMachineKey;
    let valid = true;

    if (modeRef.current === 'resize-end') {
      const proposed = shiftDate(origEndRef.current, deltaDays);
      ghostEnd = proposed < origStartRef.current ? origStartRef.current : proposed;
    } else if (modeRef.current === 'resize-start') {
      const proposed = shiftDate(origStartRef.current, deltaDays);
      ghostStart = proposed > origEndRef.current ? origEndRef.current : proposed;
    } else {
      // move: shift both dates, preserve duration; resolve target machine by hit-test
      ghostStart = shiftDate(origStartRef.current, deltaDays);
      ghostEnd = shiftDate(ghostStart, durationRef.current);
      const hit = document
        .elementFromPoint(e.clientX, e.clientY)
        ?.closest('[data-machine-key]');
      const targetKey = hit?.getAttribute('data-machine-key');
      if (targetKey) ghostMachineKey = targetKey;
      if (
        ghostMachineKey !== origMachineRef.current &&
        capableRef.current &&
        !capableRef.current.has(ghostMachineKey)
      ) {
        valid = false;
      }
    }

    setDrag({
      bookingKey: booking.MachineBookingKey,
      mode: modeRef.current,
      moved,
      ghostStart,
      ghostEnd,
      ghostMachineKey,
      originalMachineKey: origMachineRef.current,
      valid,
      pointerX: e.clientX,
      pointerY: e.clientY,
    });
  }, [setDrag]);

  const handlePointerUp = useCallback(() => {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);

    const booking = bookingRef.current;
    const drag = latestRef.current;
    setDrag(null);
    bookingRef.current = null;
    if (!booking || !drag) return;

    const { onCommit, onClick, onInvalidDrop, getMachineCode } = argsRef.current;

    // Click: a body press that never moved → open detail.
    if (!drag.moved) {
      if (drag.mode === 'move') onClick(booking);
      return;
    }

    // Invalid cross-machine drop → reject (no commit).
    if (!drag.valid) {
      onInvalidDrop(
        `${getMachineCode(drag.ghostMachineKey)} can’t run ${bookingName(booking)}.`,
      );
      return;
    }

    const name = bookingName(booking);
    const patch: BookingPlacementPatch = {};
    let label = '';

    if (drag.mode === 'resize-end') {
      if (drag.ghostEnd !== origEndRef.current) patch.EndDate = drag.ghostEnd;
      label = `Resized ${name}`;
    } else if (drag.mode === 'resize-start') {
      if (drag.ghostStart !== origStartRef.current) patch.StartDate = drag.ghostStart;
      label = `Resized ${name}`;
    } else {
      const datesChanged = drag.ghostStart !== origStartRef.current;
      const machineChanged = drag.ghostMachineKey !== origMachineRef.current;
      if (datesChanged) {
        patch.StartDate = drag.ghostStart;
        patch.EndDate = drag.ghostEnd;
      }
      if (machineChanged) patch.MachineKey = drag.ghostMachineKey;
      label = machineChanged
        ? `Moved ${name} → ${getMachineCode(drag.ghostMachineKey)}`
        : `Rescheduled ${name}`;
    }

    if (Object.keys(patch).length === 0) return; // nothing changed
    onCommit(booking, patch, label);
  }, [handlePointerMove, setDrag]);

  const beginDrag = useCallback(
    (booking: MachineBookingCalendar, mode: DragMode, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      bookingRef.current = booking;
      modeRef.current = mode;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      origStartRef.current = booking.StartDate;
      origEndRef.current = booking.EndDate;
      origMachineRef.current = booking.MachineKey;
      durationRef.current = daysBetween(booking.StartDate, booking.EndDate);
      capableRef.current = argsRef.current.getCapableMachineKeys(booking);

      setDrag({
        bookingKey: booking.MachineBookingKey,
        mode,
        moved: false,
        ghostStart: booking.StartDate,
        ghostEnd: booking.EndDate,
        ghostMachineKey: booking.MachineKey,
        originalMachineKey: booking.MachineKey,
        valid: true,
        pointerX: e.clientX,
        pointerY: e.clientY,
      });

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp, setDrag],
  );

  // Safety: detach listeners on unmount.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  return { activeDrag, beginDrag };
}
